/**
 * Money — estimate vs actual by category, and receipts (`docs/visit-page.md`
 * §12, §6, §16.5; Board 8).
 *
 * Five cards — **PRICE** (white, the customer's number) and the four internal
 * ones; **By category** (internal) with a category opening to the lines that
 * moved; then **Receipts**: on the visit the moment the crew sends them,
 * flagged *To review* until someone approves (with **Pay <name> back**) or
 * rejects (asking why). Every figure is the server's (`GET /visits/:id/money`)
 * and is printed as given; for a role without *Costs & margins* the internal
 * blocks are absent from the JSON, so they are absent here too (§2.3).
 *
 * The iPad and the phone are not drawn (lead round 1, item 7): below a 980px
 * column By category's rows become blocks and the receipts stack; on a phone
 * a receipt is a card whose press opens Board 16 D's sheet.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type {
  CostCategory, MoneyByCategory, MoneyCategoryRow, MoneyRead, MovedLine, ReceiptCard, VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Switch } from '../components/primitives.js'
import { COMMON, MONEY, RECEIPTS } from './copy.js'
import { dayText, hoursBare, hours1, money, percent, signed, signedMoney, timeIn } from './format.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { useVisitTab, useVisitWrite, type VisitWriteResult } from './api.js'
import { DeltaTag, DeltaText, KpiLabel, useBoxWidth, FieldLabel } from './time-money-parts.js'
import { InternalChip } from './internal-chip.js'
import { ReceiptSheet } from './receipt-sheet.js'
import { AddReceiptSheet } from './add-receipt.js'
import { paidWithWords, ReceiptChip, ReceiptPhoto, type ReceiptActions } from './receipt-parts.js'

/** Below this the table's rows become blocks (an iPad on its side). */
const TABLE_FROM = 980

/** Board 8's grid — *Category · Estimate · So far · Projected · vs estimate · so far, of projected*. */
const GRID = 'minmax(0,1.6fr) 128px 128px 128px 116px minmax(0,1fr)'

const CATEGORY_GLYPHS: Record<CostCategory, VisitGlyphName> = {
  labor: 'people',
  materials: 'material',
  equipment: 'loader',
  rental: 'truck',
  fees: 'receipt',
  subs: 'people',
}

export function MoneyTab({ visitId, read, phone }: { visitId: string; read: VisitRead; phone: boolean }) {
  const { data, refresh } = useVisitTab<MoneyRead>(visitId, '/money')
  const write = useVisitWrite(visitId)
  const [params, setParams] = useSearchParams()
  const [box, width] = useBoxWidth<HTMLDivElement>()
  const [sheet, setSheet] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [ringed, setRinged] = useState<string | null>(null)

  /*
   * Deep links (lead round 1 item 5, round 5 item 20): `?receipt=<id>` — the
   * Overview's **Review** and the bell — rings the card on a desktop and
   * opens Board 16 D's sheet on a phone; `?add=1` — the phone's green **Add
   * receipt** — opens Add a receipt.
   */
  const wantedReceipt = params.get('receipt')
  const wantsAdd = params.get('add') === '1'
  useEffect(() => {
    if (!data || (!wantedReceipt && !wantsAdd)) return
    if (wantsAdd) setAdding(true)
    if (wantedReceipt && data.receipts.cards.some((card) => card.id === wantedReceipt)) {
      if (phone) setSheet(wantedReceipt)
      else {
        setRinged(wantedReceipt)
        document.querySelector(`[data-receipt="${wantedReceipt}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }
    const next = new URLSearchParams(params)
    next.delete('receipt')
    next.delete('add')
    setParams(next, { replace: true })
  }, [data, wantedReceipt, wantsAdd, phone, params, setParams])

  useEffect(() => {
    if (!ringed) return
    const timer = window.setTimeout(() => setRinged(null), 1200)
    return () => window.clearTimeout(timer)
  }, [ringed])

  function written(result: VisitWriteResult) {
    refresh(result.tab as MoneyRead | undefined)
  }

  const actions: ReceiptActions = {
    approve: async (card, reimburse) => {
      written(await write('POST', `/receipts/${card.id}/approve`, { version: card.version, reimburse }, { tab: 'money' }))
    },
    reject: async (card, reason) => {
      written(await write('POST', `/receipts/${card.id}/reject`, { version: card.version, reason }, { tab: 'money' }))
    },
    undo: async (card) => {
      written(await write('POST', `/receipts/${card.id}/undo-approval`, { version: card.version }, { tab: 'money' }))
    },
  }

  if (!data) return <div ref={box} className="min-h-[200px]" />

  const narrow = phone || (width > 0 && width < TABLE_FROM)
  const open = sheet ? data.receipts.cards.find((card) => card.id === sheet) ?? null : null

  return (
    <div ref={box} className="flex min-w-0 flex-col gap-4" {...(phone ? {} : { 'data-as-drawn': 'vp-08 A' })}>
      <Kpis money={data} narrow={narrow} />
      {data.internal && <ByCategory table={data.internal.byCategory} narrow={narrow} phone={phone} />}
      {data.internal && data.internal.adjustments.length > 0 && <Adjustments money={data} read={read} />}

      <div className={cn('mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1')}>
        <b className="text-[15px]">{RECEIPTS.title}</b>
        {data.receipts.count > 0 && (
          <span className="rounded-full bg-line-soft px-[7px] py-px text-[11px] font-semibold text-text-muted">{data.receipts.count}</span>
        )}
        <span className="text-[12.5px] text-text-muted">{RECEIPTS.sub}</span>
        <span className="flex-1" />
        {!phone && data.receipts.canAdd && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-button-toolbar border
                       border-line bg-surface px-3.5 text-[12.5px] font-semibold text-text hover:bg-hover"
          >
            <VisitGlyph name="plus" size={14.5} stroke={2} />
            {RECEIPTS.add}
          </button>
        )}
      </div>

      {phone ? (
        <div className="flex flex-col gap-3">
          {data.receipts.cards.map((card) => (
            <PhoneReceipt key={card.id} card={card} read={read} onOpen={() => setSheet(card.id)} />
          ))}
        </div>
      ) : (
        <div className={cn('flex items-stretch gap-4', narrow && 'flex-col')}>
          {data.receipts.cards.map((card) => (
            <ReceiptBox key={card.id} card={card} read={read} actions={actions} ringed={ringed === card.id} />
          ))}
        </div>
      )}

      {open && (
        <ReceiptSheet card={open} read={read} actions={actions} onClose={() => setSheet(null)} />
      )}
      {adding && (
        <AddReceiptSheet
          visitId={visitId}
          read={read}
          money={data}
          phone={phone}
          onClose={() => setAdding(false)}
          onAdded={(result) => { written(result); setAdding(false) }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The five cards
// ---------------------------------------------------------------------------

function Kpi({ label, internal, value, tag, sub, wide = false }: {
  label: string; internal: boolean; value: string; tag?: ReactNode; sub: string | null
  /** The price across both columns when the five fold to two (not drawn — lead round 1, item 7). */
  wide?: boolean
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5 rounded-record border border-line px-4 py-[13px]',
      internal ? 'bg-surface-internal' : 'bg-surface', wide && 'col-span-2')} data-money-kpi={label}>
      <div className="flex items-center gap-1.5">
        <KpiLabel>{label}</KpiLabel>
        <span className="flex-1" />
        {internal && <InternalChip />}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <b className="text-[22px] tracking-[-.01em]">{value}</b>
        {tag}
      </div>
      {sub && <span className="text-[12px] text-text-muted">{sub}</span>}
    </div>
  )
}

/** **PRICE** · **ESTIMATED COST** · **SO FAR** · **PROJECTED** · **MARGIN** — the last four internal (Board 8). */
function Kpis({ money: read, narrow }: { money: MoneyRead; narrow: boolean }) {
  const { price } = read
  const cards = read.internal?.cards
  return (
    <div className={cn('grid gap-3', narrow ? 'grid-cols-2' : cards ? 'grid-cols-5' : 'grid-cols-1')}>
      <Kpi
        wide={narrow}
        label={MONEY.kpi.price}
        internal={false}
        value={price.price ? money(price.price) : ''}
        sub={price.billing === 'fixed' && price.quoteReference && price.approvedOn
          ? MONEY.kpi.priceSub(price.quoteReference, dayText(price.approvedOn, 'monthDay'))
          : null}
      />
      {cards && (
        <>
          <Kpi label={MONEY.kpi.estimatedCost} internal value={money(cards.estimated)} sub={MONEY.kpi.estimatedCostSub} />
          <Kpi label={MONEY.kpi.soFar} internal value={money(cards.soFar)} sub={MONEY.kpi.soFarSub} />
          <Kpi
            label={MONEY.kpi.projected}
            internal
            value={money(cards.projected)}
            tag={cards.projectedDelta.tone === 'zero' ? undefined
              : <DeltaTag tone={cards.projectedDelta.tone}>{signedMoney(cards.projectedDelta)}</DeltaTag>}
            sub={MONEY.kpi.projectedSub}
          />
          <Kpi
            label={MONEY.kpi.margin}
            internal
            value={percent(cards.margin.projected)}
            tag={cards.margin.delta.tone === 'zero' ? undefined
              : <DeltaTag tone={cards.margin.delta.tone}>{COMMON.pts(signed(cards.margin.delta))}</DeltaTag>}
            sub={MONEY.kpi.marginSub(percent(cards.margin.estimated))}
          />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// By category
// ---------------------------------------------------------------------------

/** The line under a category's name (Board 8). */
function subOf(row: MoneyCategoryRow): string | null {
  const { sub } = row
  switch (sub.key) {
    case 'labor': return MONEY.sub.labor(hours1(sub.estimatedHours), hours1(sub.soFarHours), hours1(sub.projectedHours))
    case 'materials': return MONEY.sub.materials(String(sub.lines))
    case 'equipment': return MONEY.sub.equipment(hoursBare(sub.plannedHours), hoursBare(sub.usedHours))
    case 'rental': {
      const parts = sub.rentals.map((rental) => MONEY.sub.rental(rental.name, COMMON.list(rental.days.map((day) => dayText(day, 'day')))))
      return parts.length ? parts.reduce((a, b) => COMMON.dot(a, b)) : null
    }
    case 'fees': {
      const parts = [
        ...sub.lines.map((line) => MONEY.sub.feesPlanned(line.name, String(line.qty), money(line.unitCost))),
        ...sub.spent.map((spent) => MONEY.sub.feeSpent(dayText(spent.on, 'day'), money(spent.amount))),
      ]
      return parts.length ? parts.reduce((a, b) => COMMON.dot(a, b)) : null
    }
    case 'subs': return null
  }
}

function ByCategory({ table, narrow, phone }: { table: MoneyByCategory; narrow: boolean; phone: boolean }) {
  const navigate = useNavigate()
  const [opened, setOpened] = useState<Set<CostCategory>>(
    () => new Set(table.categories.filter((row) => row.moved.length > 0).map((row) => row.key)),
  )
  const toggle = (key: CostCategory) => setOpened((was) => {
    const next = new Set(was)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })

  return (
    <section className="overflow-hidden rounded-record border border-line bg-surface-internal" data-money-by-category="">
      <header className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-line', phone ? 'px-3.5 py-3' : 'px-[18px] py-[13px]')}>
        <b className="text-[14.5px]">{MONEY.byCategory.title}</b>
        <InternalChip />
        {table.quoteReference && <span className="text-[12px] text-text-muted">{MONEY.byCategory.sub(table.quoteReference)}</span>}
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => navigate(`/jobs/${table.job.id}?tab=money`)}
          className="inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-accent hover:text-accent-dark"
        >
          <VisitGlyph name="chevronRight" size={13} stroke={2.2} />
          {MONEY.byCategory.openJob(table.job.reference)}
        </button>
      </header>

      {!narrow && (
        <div
          className="grid h-[35px] items-center gap-3 border-b border-line bg-surface-alt px-[18px] text-[10.5px] font-semibold uppercase
                     tracking-[.06em] text-text-faint"
          style={{ gridTemplateColumns: GRID }}
        >
          <span className="whitespace-nowrap">{MONEY.byCategory.head.category}</span>
          <span className="whitespace-nowrap text-right">{MONEY.byCategory.head.estimate}</span>
          <span className="whitespace-nowrap text-right">{MONEY.byCategory.head.soFar}</span>
          <span className="whitespace-nowrap text-right">{MONEY.byCategory.head.projected}</span>
          <span className="whitespace-nowrap text-right">{MONEY.byCategory.head.vsEstimate}</span>
          <span className="whitespace-nowrap">{MONEY.byCategory.head.ofProjected}</span>
        </div>
      )}

      {table.categories.map((row) => {
        const isOpen = opened.has(row.key)
        const opens = row.moved.length > 0 || row.asPlanned > 0
        return (
          <div key={row.key} data-money-category={row.key}>
            {narrow
              ? <CategoryBlock row={row} open={isOpen} opens={opens} onToggle={() => toggle(row.key)} phone={phone} />
              : <CategoryRow row={row} open={isOpen} opens={opens} onToggle={() => toggle(row.key)} />}
            {isOpen && row.moved.map((line) => (narrow
              ? <MovedBlock key={line.lineId ?? lineName(line)} line={line} phone={phone} />
              : <MovedRow key={line.lineId ?? lineName(line)} line={line} />))}
            {isOpen && row.asPlanned > 0 && (
              <div className={cn('flex min-h-[34px] items-center border-b border-line-faint bg-green-band-bg',
                narrow ? (phone ? 'px-3.5 py-2' : 'px-[18px] py-2') : 'px-[18px] py-2')}>
                <span className={cn('text-[12px] text-text-muted', !phone && 'pl-[38px]')}>{MONEY.moved.more(String(row.asPlanned))}</span>
              </div>
            )}
          </div>
        )
      })}

      <Totals table={table} narrow={narrow} phone={phone} />
    </section>
  )
}

function CategoryName({ row }: { row: MoneyCategoryRow }) {
  const sub = subOf(row)
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] border border-line bg-surface text-text-muted">
        <VisitGlyph name={CATEGORY_GLYPHS[row.key]} size={14} stroke={2} />
      </span>
      <div className="flex min-w-0 flex-col">
        <b className="text-[13px]">{MONEY.categories[row.key]}</b>
        {sub && <span className="text-[11.5px] text-text-muted">{sub}</span>}
      </div>
    </div>
  )
}

function OfProjected({ row }: { row: MoneyCategoryRow }) {
  return (
    <div className="flex w-full items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="relative h-1.5 w-full rounded-full bg-line-soft">
          <div className="h-full rounded-full bg-accent" style={{ width: `${row.ofProjected.barPct}%` }} />
        </div>
      </div>
      <span className="w-[34px] whitespace-nowrap text-right text-[11px] text-text-muted">{`${row.ofProjected.percent}%`}</span>
    </div>
  )
}

const cell = 'flex min-w-0 items-center justify-end whitespace-nowrap text-right'

function CategoryRow({ row, open, opens, onToggle }: {
  row: MoneyCategoryRow; open: boolean; opens: boolean; onToggle: () => void
}) {
  const Row = opens ? 'button' : 'div'
  return (
    <Row
      {...(opens ? { type: 'button' as const, onClick: onToggle, 'aria-expanded': open } : {})}
      className={cn('grid min-h-[54px] w-full items-stretch gap-3 border-b border-line-faint px-[18px] py-2 text-left text-[13px]',
        open && 'bg-green-band-bg', opens && 'hover:bg-hover')}
      style={{ gridTemplateColumns: GRID }}
    >
      <div className="flex min-w-0 items-center"><CategoryName row={row} /></div>
      <div className={cell}>{money(row.estimate)}</div>
      <div className={cell}>{money(row.soFar)}</div>
      <div className={cn(cell, 'font-bold')}>{money(row.projected)}</div>
      <div className={cell}><DeltaText delta={row.delta} text={signedMoney(row.delta)} /></div>
      <div className="flex min-w-0 items-center"><OfProjected row={row} /></div>
    </Row>
  )
}

function lineName(line: MovedLine): string {
  if (line.kind === 'swapped') {
    const to = line.names.model ? COMMON.commaList([line.names.model, ...(line.names.detail ? [line.names.detail] : [])]) : line.names.name
    return MONEY.moved.swapped(line.names.shortName, to)
  }
  if (line.kind === 'bought') return MONEY.moved.bought(line.names.shortName)
  return line.name
}

function lineSub(line: MovedLine): string | null {
  if (line.kind === 'swapped') return line.billing === 'time_materials' ? null : MONEY.moved.swappedFixed
  if (line.kind === 'bought') {
    return line.receipt.state === 'to_review'
      ? MONEY.moved.receipt(line.receipt.vendor, money(line.receipt.amount))
      : MONEY.moved.receiptOnly(line.receipt.vendor, money(line.receipt.amount))
  }
  return null
}

function MovedRow({ line }: { line: MovedLine }) {
  const sub = lineSub(line)
  return (
    <div
      className="grid min-h-11 items-stretch gap-3 border-b border-line-faint bg-green-band-bg px-[18px] py-2 text-[13px]"
      style={{ gridTemplateColumns: GRID }}
      data-money-moved={line.lineId ?? ''}
    >
      <div className="flex min-w-0 items-center">
        <div className="flex min-w-0 flex-col pl-[38px]">
          <span className="text-[12.5px] font-semibold">{lineName(line)}</span>
          {sub && <span className="text-[11.5px] text-text-muted">{sub}</span>}
        </div>
      </div>
      <div className={cn(cell, 'text-[12.5px]')}>{line.estimate ? money(line.estimate) : ''}</div>
      <div className={cn(cell, 'text-[12.5px]')}>{line.soFar ? money(line.soFar) : ''}</div>
      <div className={cn(cell, 'text-[12.5px]')}>{money(line.projected)}</div>
      <div className={cell}><DeltaText delta={line.delta} text={signedMoney(line.delta)} /></div>
      <div />
    </div>
  )
}

/** Estimate · So far · Projected as three labelled figures — the narrow reading of a row. */
function Figures({ estimate, soFar, projected, bold = false }: {
  estimate: string | null; soFar: string | null; projected: string | null; bold?: boolean
}) {
  const figure = (label: string, value: string | null, strong: boolean) => (
    <div className="flex min-w-0 flex-col">
      <span className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">{label}</span>
      <span className={cn('whitespace-nowrap text-[13px]', strong && 'font-bold')}>{value ?? ''}</span>
    </div>
  )
  return (
    <div className="grid grid-cols-3 gap-3">
      {figure(MONEY.byCategory.head.estimate, estimate, bold)}
      {figure(MONEY.byCategory.head.soFar, soFar, bold)}
      {figure(MONEY.byCategory.head.projected, projected, true)}
    </div>
  )
}

function CategoryBlock({ row, open, opens, onToggle, phone }: {
  row: MoneyCategoryRow; open: boolean; opens: boolean; onToggle: () => void; phone: boolean
}) {
  const Row = opens ? 'button' : 'div'
  return (
    <Row
      {...(opens ? { type: 'button' as const, onClick: onToggle, 'aria-expanded': open } : {})}
      className={cn('flex w-full flex-col gap-2.5 border-b border-line-faint text-left', phone ? 'px-3.5 py-3' : 'px-[18px] py-3',
        open && 'bg-green-band-bg')}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1"><CategoryName row={row} /></div>
        <DeltaText delta={row.delta} text={signedMoney(row.delta)} className="text-[13px]" />
      </div>
      <Figures estimate={money(row.estimate)} soFar={money(row.soFar)} projected={money(row.projected)} />
      <OfProjected row={row} />
    </Row>
  )
}

function MovedBlock({ line, phone }: { line: MovedLine; phone: boolean }) {
  const sub = lineSub(line)
  return (
    <div className={cn('flex flex-col gap-2 border-b border-line-faint bg-green-band-bg', phone ? 'px-3.5 py-3' : 'px-[18px] py-3')}>
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[12.5px] font-semibold">{lineName(line)}</span>
          {sub && <span className="text-[11.5px] text-text-muted">{sub}</span>}
        </div>
        <DeltaText delta={line.delta} text={signedMoney(line.delta)} className="text-[13px]" />
      </div>
      <Figures
        estimate={line.estimate ? money(line.estimate) : null}
        soFar={line.soFar ? money(line.soFar) : null}
        projected={money(line.projected)}
      />
    </div>
  )
}

/** **Total cost** · **Earns** *· price $14,250.00 less cost* · **Margin** (Board 8). */
function Totals({ table, narrow, phone }: { table: MoneyByCategory; narrow: boolean; phone: boolean }) {
  const { total, earns, margin } = table
  if (narrow) {
    const block = cn('flex flex-col gap-2', phone ? 'px-3.5 py-3' : 'px-[18px] py-3')
    return (
      <>
        <div className={cn(block, 'border-t border-line bg-surface-internal-alt')}>
          <div className="flex items-center gap-3">
            <b className="flex-1 text-[13.5px]">{MONEY.totalCost}</b>
            <DeltaText delta={total.delta} text={signedMoney(total.delta)} className="text-[13px]" />
          </div>
          <Figures estimate={money(total.estimate)} soFar={money(total.soFar)} projected={money(total.projected)} bold />
        </div>
        <div className={cn(block, 'border-t border-line-faint')}>
          <div className="flex items-center gap-3">
            <span className="flex-1 text-[13px]">{MONEY.earns} <span className="text-text-muted">{MONEY.earnsSub(money(earns.price))}</span></span>
            <DeltaText delta={earns.delta} text={signedMoney(earns.delta)} className="text-[13px]" />
          </div>
          <Figures estimate={money(earns.estimate)} soFar={null} projected={money(earns.projected)} />
        </div>
        <div className={cn(block, 'border-t border-line-faint')}>
          <div className="flex items-center gap-3">
            <span className="flex-1 text-[13px]">{MONEY.margin}</span>
            <DeltaText delta={margin.delta} text={COMMON.pts(signed(margin.delta))} className="text-[13px]" />
          </div>
          <Figures estimate={percent(margin.estimate)} soFar={null} projected={percent(margin.projected)} />
        </div>
      </>
    )
  }
  // Board 8's rows are 50 and 44 high inside their top border.
  const row = 'grid items-center gap-3 px-[18px]'
  return (
    <>
      <div className={cn(row, 'h-[51px] border-t border-line bg-surface-internal-alt')} style={{ gridTemplateColumns: GRID }}>
        <b className="text-[13.5px]">{MONEY.totalCost}</b>
        <span className={cn(cell, 'text-[13px] font-bold')}>{money(total.estimate)}</span>
        <span className={cn(cell, 'text-[13px] font-bold')}>{money(total.soFar)}</span>
        <span className={cn(cell, 'text-[13px] font-bold')}>{money(total.projected)}</span>
        <span className={cell}><DeltaText delta={total.delta} text={signedMoney(total.delta)} /></span>
      </div>
      <div className={cn(row, 'h-[45px] border-t border-line-faint')} style={{ gridTemplateColumns: GRID }}>
        <span className="text-[13px]">{MONEY.earns} <span className="text-text-muted">{MONEY.earnsSub(money(earns.price))}</span></span>
        <span className={cn(cell, 'text-[13px]')}>{money(earns.estimate)}</span>
        <span />
        <span className={cn(cell, 'text-[13px] font-bold')}>{money(earns.projected)}</span>
        <span className={cell}><DeltaText delta={earns.delta} text={signedMoney(earns.delta)} /></span>
      </div>
      <div className={cn(row, 'h-[45px] border-t border-line-faint')} style={{ gridTemplateColumns: GRID }}>
        <span className="text-[13px]">{MONEY.margin}</span>
        <span className={cn(cell, 'text-[13px]')}>{percent(margin.estimate)}</span>
        <span />
        <span className={cn(cell, 'text-[13px] font-bold')}>{percent(margin.projected)}</span>
        <span className={cell}><DeltaText delta={margin.delta} text={COMMON.pts(signed(margin.delta))} /></span>
      </div>
    </>
  )
}

/** §16.5: the adjustments after close, under the table — and in History. */
function Adjustments({ money: read, read: visit }: { money: MoneyRead; read: VisitRead }) {
  const rows = read.internal?.adjustments ?? []
  return (
    <section className="overflow-hidden rounded-record border border-line bg-surface-internal" data-money-adjustments="">
      <header className="flex items-center gap-2 border-b border-line px-[18px] py-[13px]">
        <b className="text-[14.5px]">{MONEY.adjustments}</b>
        <InternalChip />
      </header>
      {rows.map((row) => (
        <div key={row.id} className="flex items-center gap-3 border-b border-line-faint px-[18px] py-2.5 text-[13px] last:border-b-0">
          <div className="flex min-w-0 flex-1 flex-col">
            <b className="text-[13px]">{MONEY.categories[row.category]}</b>
            <span className="text-[11.5px] text-text-muted">
              {COMMON.dot(row.reason, COMMON.dot(row.by.name, timeIn(visit.timeZone, row.at, 'dateAmpm')))}
            </span>
          </div>
          <DeltaText delta={row.amount} text={signedMoney(row.amount)} />
        </div>
      ))}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Receipts
// ---------------------------------------------------------------------------

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2.5 py-[5px] text-[13px] leading-[1.35]">
      <span className="w-24 shrink-0 text-text-muted">{label}</span>
      <span className="min-w-0 flex-1 text-text">{children}</span>
    </div>
  )
}

/** *Construction adhesive · planned $45.00 ·* **+$19.52** — the plan line, its estimate and the difference (internal). */
function Against({ card }: { card: ReceiptCard }) {
  if (!card.against) return null
  const internal = card.internal
  if (!internal?.planned) return <>{card.against.name}</>
  return (
    <>
      {RECEIPTS.againstLineShort(card.against.name, money(internal.planned))}
      {internal.delta && internal.delta.tone !== 'zero' && (
        <>{' · '}<b className={internal.delta.tone === 'over' ? 'text-warn' : 'text-accent-dark'}>{signedMoney(internal.delta)}</b></>
      )}
    </>
  )
}

const SMALL = 'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] border px-3.5 text-[12.5px] font-semibold'

/** A receipt on a desktop or an iPad (Board 8). */
function ReceiptBox({ card, read, actions, ringed }: {
  card: ReceiptCard; read: VisitRead; actions: ReceiptActions; ringed: boolean
}) {
  const [payBack, setPayBack] = useState(card.reimburse)
  const [rejecting, setRejecting] = useState(false)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const node = useRef<HTMLElement>(null)
  useEffect(() => setPayBack(card.reimburse), [card.reimburse, card.version])

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setProblem(null)
    try {
      await action()
      setRejecting(false)
      setWhy('')
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const toReview = card.state === 'to_review'
  return (
    <article
      ref={node}
      data-receipt={card.id}
      className={cn('flex min-w-0 flex-1 basis-0 gap-4 rounded-record border bg-surface px-4 py-3.5 transition-shadow',
        toReview ? 'border-warn-line' : 'border-line', ringed && 'ring-2 ring-accent-ring')}
    >
      <ReceiptPhoto card={card} width={112} height={150} />
      <div className="flex min-w-0 flex-1 flex-col gap-[9px]">
        <div className="flex items-center gap-2">
          <b className="truncate text-[14px]">{card.vendor}</b>
          <span className="flex-1" />
          <ReceiptChip card={card} />
        </div>
        <div className="flex flex-col">
          {card.internal && <Fact label={RECEIPTS.amount}><b>{money(card.internal.amount)}</b></Fact>}
          <Fact label={RECEIPTS.when}>{RECEIPTS.at(timeIn(read.timeZone, card.at, 'dateAmpm'), String(card.day))}</Fact>
          {card.sentBy && <Fact label={RECEIPTS.sentBy}>{card.sentBy.name}</Fact>}
          <Fact label={RECEIPTS.for}>{card.description}</Fact>
          <Fact label={RECEIPTS.paidWith}>{paidWithWords(card)}</Fact>
          {card.against && <Fact label={RECEIPTS.against}><Against card={card} /></Fact>}
        </div>

        {toReview && card.canReview && !rejecting && card.sentBy && (
          <div className="flex items-center gap-2.5 rounded-[10px] border border-line-faint bg-surface-alt px-3 py-[9px]">
            <Switch checked={payBack} onChange={setPayBack} label={RECEIPTS.payBack(card.sentBy.firstName)} hideLabel size="sm" />
            <span className="text-[12.5px]"><b>{RECEIPTS.payBack(card.sentBy.firstName)}</b> {RECEIPTS.payBackSub}</span>
          </div>
        )}

        {toReview && card.canReview && !rejecting && (
          <div className="flex items-center gap-2">
            <span className="flex-1 text-[11.5px] leading-[1.4] text-text-muted">
              {card.flaggedSince ? RECEIPTS.flagged(timeIn(read.timeZone, card.flaggedSince, 'dayAmpm')) : ''}
            </span>
            <button type="button" disabled={busy} onClick={() => setRejecting(true)}
              className={cn(SMALL, 'border-line bg-surface text-text hover:bg-hover')}>
              {RECEIPTS.reject}
            </button>
            <button type="button" disabled={busy} onClick={() => void run(() => actions.approve(card, payBack))}
              className={cn(SMALL, 'border-accent bg-accent text-white hover:bg-accent-dark')}>
              <VisitGlyph name="check" size={14.5} stroke={2} />
              {RECEIPTS.approve}
            </button>
          </div>
        )}

        {rejecting && (
          <div className="flex flex-col gap-2">
            <div>
              <FieldLabel htmlFor={`reject-${card.id}`} required>{COMMON.why}</FieldLabel>
              <textarea id={`reject-${card.id}`} value={why} autoFocus onChange={(event) => setWhy(event.target.value)}
                className="block h-14 w-full resize-none rounded-button tabular-nums border border-line bg-surface px-3 py-2.5 text-[13px] leading-normal
                           text-text outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]" />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => { setRejecting(false); setWhy('') }}
                className={cn(SMALL, 'border-line bg-surface text-text hover:bg-hover')}>
                {COMMON.cancel}
              </button>
              <button type="button" disabled={busy || !why.trim()} onClick={() => void run(() => actions.reject(card, why.trim()))}
                className={cn(SMALL, why.trim() ? 'border-danger-line bg-surface text-danger hover:bg-danger-bg'
                  : 'cursor-not-allowed border-line bg-line-soft text-text-faint')}>
                {RECEIPTS.reject}
              </button>
            </div>
          </div>
        )}

        {card.state === 'approved' && card.reviewed && (
          <>
            <div className="flex items-center gap-2 rounded-[10px] border border-green-band-line bg-green-band-bg px-3 py-[9px]
                            text-[12.5px] text-accent-dark">
              <VisitGlyph name="checkCircle" size={15} stroke={2} />
              {RECEIPTS.approvedBy(card.reviewed.by.name, timeIn(read.timeZone, card.reviewed.at, 'dayAmpm'))}
            </div>
            <div className="flex items-center gap-2">
              <span className="flex-1 text-[11.5px] text-text-muted">
                {card.paidWith === 'company_card' && !card.reimburse ? RECEIPTS.nothingToPayBack : ''}
              </span>
              {card.canReview && (
                <button type="button" disabled={busy} onClick={() => void run(() => actions.undo(card))}
                  className="inline-flex h-[30px] items-center rounded-button px-2 text-[12px] font-semibold text-text-muted hover:bg-hover">
                  {RECEIPTS.undoApproval}
                </button>
              )}
            </div>
          </>
        )}

        {problem && <p className="m-0 text-[12.5px] text-danger">{problem}</p>}
      </div>
    </article>
  )
}

/** A receipt on a phone — a card whose press opens Board 16 D's sheet (not drawn: lead round 1, item 7). */
function PhoneReceipt({ card, read, onOpen }: { card: ReceiptCard; read: VisitRead; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-receipt={card.id}
      className={cn('flex min-h-11 w-full gap-3 rounded-record border bg-surface p-3 text-left',
        card.state === 'to_review' ? 'border-warn-line' : 'border-line')}
    >
      <ReceiptPhoto card={card} width={56} height={75} />
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className="flex items-center gap-2">
          <b className="min-w-0 flex-1 truncate text-[14.5px]">{card.vendor}</b>
          <ReceiptChip card={card} />
        </div>
        {card.internal && <b className="text-[14px]">{money(card.internal.amount)}</b>}
        <span className="text-[12.5px] text-text-muted">{RECEIPTS.at(timeIn(read.timeZone, card.at, 'dateAmpm'), String(card.day))}</span>
        {card.sentBy && <span className="text-[12.5px] text-text-muted">{card.sentBy.name}</span>}
      </div>
    </button>
  )
}
